// src/components/Pages/MethodCase.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MathJaxContext } from "better-react-mathjax";
import TabPanelEnter from "../Visualeffects/TabPanelEnter";
import MethodLongExplanationSection from "../Methods/MethodLongExplanationSection";

export default function MethodCase({
  tabKey,
  anchorEl,
  state,
  setState,
  UI: UIComp,
  Explanation: ExplComp,
  LongExplanation: LongExplComp,
  Animation: AnimationComp,
  title,
  Intro,
  flexWidth = "w-full",
  leftWidth = "w-full xl:flex-1 xl:min-w-0",
  colorClasses = "bg-white/80 border-white/40 text-slate-900 backdrop-blur-md",
  enterMs = 2000,
  enterEase = "cubic-bezier(.25,.9,.2,1)",
  animationContainerClass = "bg-white/10 border-white/30 backdrop-blur-sm",
  asAbsolute = false,
  visible = true,
  exitMs,
  initialEntered = false,
  hideExplanation = false,
}) {
  // --- Konstanten ---
  const DESKTOP_GAP = 24;           // gap-6
  const EXPLANATION_WIDTH = 0;
  const MIN_SOLVER_DESKTOP = 560;   // gewünschte min Breite des Solvers auf Desktop
  const MOBILE_TRIGGER = MIN_SOLVER_DESKTOP + 800; // früherer Mobile-Switch
  const SAFE_PADDING = 16;          // Puffer, bevor “rechter Rand berührt”
  const DESKTOP_SHIFT_PX = 0;

  const containerRef = useRef(null);
  const solverCardRef = useRef(null);

  const mathJaxConfig = useMemo(
    () => ({
      loader: { load: ["input/tex", "output/chtml"] },
      tex: {
        inlineMath: [["\\(", "\\)"], ["$", "$"]],
        displayMath: [["\\[", "\\]"], ["$$", "$$"]],
      },
    }),
    []
  );

  // Viewportbreite
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Containerbreite messen
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const updateWidth = () => {
      const width = node.getBoundingClientRect().width;
      setContainerWidth((prev) => (prev !== width ? width : prev));
    };
    updateWidth();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(updateWidth);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // "Natürliche" Solverbreite stabil erfassen (max gesehene, nur wenn Side-by-Side passt)
  const [solverNaturalWidth, setSolverNaturalWidth] = useState(0);
  useLayoutEffect(() => {
    const node = solverCardRef.current;
    if (!node) return;

    const updateWidth = () => {
      const width = node.getBoundingClientRect().width;
      if (width <= 0) return;

      const hasDesktopSpaceNow = viewportWidth >= 1280;
      const sideBySideFitsNow =
        hasDesktopSpaceNow &&
        containerWidth >= width + DESKTOP_GAP + EXPLANATION_WIDTH;

      if (!sideBySideFitsNow) return;

      setSolverNaturalWidth((prev) => (width > prev ? width : prev || width));
    };

    updateWidth();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(updateWidth);
    ro.observe(node);
    return () => ro.disconnect();
  }, [containerWidth, viewportWidth]);

  // --- Umschaltlogik ---
  const hasDesktopSpace = viewportWidth >= 1280;

  // gewünschte “ideale” Solverbreite auf Desktop
  const desiredSolverWidth = Math.max(MIN_SOLVER_DESKTOP, solverNaturalWidth || 0);

  // Kann Side-by-Side (Solver + GAP + Erklärung) überhaupt passen?
  const canPlaceSideBySide =
    hasDesktopSpace &&
    containerWidth > 0 &&
    desiredSolverWidth > 0 &&
    containerWidth >= desiredSolverWidth + DESKTOP_GAP + EXPLANATION_WIDTH;

  // alter Effekt: wenn Side-by-Side nicht passt, stacken (Erklärung unten)
  const isStacked = hasDesktopSpace ? !canPlaceSideBySide : true;

  // NEU: Würde der Solver im *gestapelten* Desktop gequetscht werden,
  // weil der Container kleiner als die gewünschte Solverbreite wird?
  // Dann lieber sofort auf mobile wechseln – noch VOR dem Schrumpfen.
  const wouldCompressStacked =
    hasDesktopSpace &&
    isStacked &&
    containerWidth > 0 &&
    containerWidth < desiredSolverWidth + SAFE_PADDING;

  // Mobile-UI Bedingungen:
  // - echter Mobilbreakpoint (<768)
  // - früherer globaler Trigger
  // - (NEU) im gestapelten Desktop, sobald der Solver sonst schrumpfen müsste
  const isMobile =
    viewportWidth < 768 ||
    viewportWidth < MOBILE_TRIGGER ||
    wouldCompressStacked;

  const shouldShiftHorizontally = hasDesktopSpace && !isStacked;
  const solverShift = shouldShiftHorizontally ? DESKTOP_SHIFT_PX : 0;

  // --- Layoutklassen & feste Breite im Stacked-Desktop ---
  const layoutClass = ["flex flex-col items-center gap-8 px-4", asAbsolute ? "w-full" : flexWidth];
  if (!asAbsolute) {
    layoutClass.push("mx-auto");
    layoutClass.push("mt-10");
  }
  if (hasDesktopSpace && !isStacked) {
    layoutClass.push("xl:flex-row", "xl:items-start", "xl:justify-center");
  } else if (hasDesktopSpace) {
    layoutClass.push("xl:items-start");
  }

  // Im gestapelten Desktop: Solver fix auf gewünschte Breite pinnen.
  // Er wird NICHT schmaler – bevor das passiert, greift isMobile (wouldCompressStacked).
  const fixedStackedWidthPx =
    hasDesktopSpace && isStacked
      ? desiredSolverWidth
      : undefined;

  // Linke Spalte
  const leftContainerClass = useMemo(() => {
    const classes = [leftWidth];
    classes.push("mx-auto");
    if (hasDesktopSpace && isStacked) {
      classes.push("xl:flex-none xl:w-auto xl:self-start");
    }
    return classes.join(" ");
  }, [leftWidth, hasDesktopSpace, isStacked]);

  // Im gestapelten Desktop limitieren wir kosmetisch die Karte auf die feste Breite
  const stackedMaxWidth = useMemo(() => {
    if (!hasDesktopSpace || !isStacked) return undefined;
    return fixedStackedWidthPx;
  }, [hasDesktopSpace, isStacked, fixedStackedWidthPx]);

  const solverWrapperStyle = {
    transform: solverShift ? `translateX(${solverShift}px)` : undefined,
    width: !isMobile && fixedStackedWidthPx ? `${fixedStackedWidthPx}px` : undefined,
  };

  const solverCardStyle = {
    minWidth: !isMobile ? `${MIN_SOLVER_DESKTOP}px` : undefined,
    maxWidth: stackedMaxWidth ? `${stackedMaxWidth}px` : undefined,
  };

  const renderSolverCard = () => (
    <div style={solverWrapperStyle} className="relative">
      <div
        ref={solverCardRef}
        className={`relative ${asAbsolute ? 'pointer-events-none' : ''}`}
        style={solverCardStyle}
      >
        <UIComp
          state={state}
          setState={setState}
        />
      </div>
    </div>
  );

  const solverBlock = isMobile ? (
    <div
      className={leftContainerClass}
      style={solverShift ? { transform: `translateX(${solverShift}px)` } : undefined}
    >
      {renderSolverCard()}
    </div>
  ) : (
    <TabPanelEnter
      key={tabKey}
      anchorEl={anchorEl}
      variant="circle"
      durationMs={enterMs}
      exitMs={exitMs ?? enterMs}
      ease={enterEase}
      className={leftContainerClass}
      visible={visible}
      initialEntered={initialEntered}
    >
      {renderSolverCard()}
    </TabPanelEnter>
  );

  const explanationContent =
    !hideExplanation && ExplComp ? (
      <section className="mt-10 w-full">
        <div className="mx-auto w-full max-w-[1400px] px-4">
          <div className={`rounded-3xl ${colorClasses} p-6 shadow-[0_35px_90px_-45px_rgba(15,20,43,0.85)]`}>
            <ExplComp />
          </div>
        </div>
      </section>
    ) : null;

  const headerSection = !asAbsolute ? (
    <MathJaxContext version={3} config={mathJaxConfig} hideUntilTypeset="first">
      <section className="mx-auto mt-12 w-full max-w-[1400px] px-4 text-white">
        <div className="relative text-white">
          {title && <h1 className="text-3xl font-bold tracking-tight text-white">{title}</h1>}
          {AnimationComp && (
            <div
              className={`mt-6 w-full max-w-[340px] overflow-hidden rounded-3xl border p-4 shadow-lg mx-auto lg:float-right lg:ml-10 lg:mt-0 lg:mx-0 ${animationContainerClass}`}
            >
              <AnimationComp isActive />
            </div>
          )}
          {Intro && (
            <div className="mt-4 text-lg leading-relaxed text-white/80">
              <Intro lite />
            </div>
          )}
          <div className="clear-both" />
        </div>
      </section>
    </MathJaxContext>
  ) : null;

  const content = (
    <>
      {headerSection}
      <div ref={containerRef} className={layoutClass.join(" ")}>
        {solverBlock}
      </div>

      {!asAbsolute && LongExplComp && (
        <>
          <MethodLongExplanationSection LongExplanation={LongExplComp} />
          {explanationContent}
        </>
      )}

      {!asAbsolute && !LongExplComp && explanationContent}
    </>
  );

  if (asAbsolute) {
    return <div className="absolute inset-0 z-50 pointer-events-none">{content}</div>;
  }

  return <div className="relative">{content}</div>;
}
